import { useCallback } from "react";

import { selectSuggestion } from "../../AutocompleteDropdown/selectSuggestion";
import type { SuggestionItem } from "../../AutocompleteDropdown/types";
import { useStructuredSuggestions } from "../../AutocompleteDropdown/useStructuredSuggestions";
import { getOperatorsForFilter } from "../../config/schema-utils";
import { getFilterDefinitionByKey } from "../../config/structured-search-schema";
import { useStructuredSearchStore } from "../../stores";
import { isStructuredSearchApplyShortcut } from "../value-utils";

const isDateValueMode = (store: ReturnType<typeof useStructuredSearchStore>): boolean => {
  if (store.step !== "value" || !store.inProgress.type || !store.inProgress.operator) {
    return false;
  }
  const ops = getOperatorsForFilter(store.schema, store.inProgress.field);
  const opDef = ops.find(o => o.operator === store.inProgress.operator);
  const valueInput = opDef?.valueInput;
  return valueInput === "date" || valueInput === "daterange";
};

const canBackspaceRemoveChip = (store: ReturnType<typeof useStructuredSearchStore>): boolean => {
  const hasRemovableChip = store.hasFilters || Boolean(store.q.trim());
  if (!hasRemovableChip) {
    return false;
  }
  if (isDateValueMode(store)) {
    return true;
  }
  return (
    !store.inputText &&
    (store.step === "idle" || (store.step === "field" && !store.inProgress.field))
  );
};

export const useStructuredSearchKeyboard = (getInputRef: () => HTMLInputElement | null) => {
  const store = useStructuredSearchStore();
  const {
    suggestions,
    nestedKeyMode,
    isPromptField,
    isTraceVersionedEntityField,
    promptVersionStep,
    isAwaitingCurrentInputSuggestions,
  } = useStructuredSuggestions();
  const isPlainTextSearchSuggestion = (
    item: SuggestionItem,
  ): item is Extract<SuggestionItem, { type: "plain_text_search" }> =>
    "type" in item && item.type === "plain_text_search";

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.defaultPrevented) {
        return;
      }

      if (isStructuredSearchApplyShortcut(e) && store.step === "value") {
        e.preventDefault();
        store.completeFilter();
        return;
      }

      if (e.key === "Escape" && store.editingFilterId) {
        e.preventDefault();
        store.cancelEdit();
        return;
      }

      if (e.key === "Escape" && store.step === "field") {
        e.preventDefault();
        store.cancelBuilder();
        getInputRef()?.blur();
        return;
      }

      if (e.key === "Escape" && store.step !== "idle") {
        e.preventDefault();
        store.cancelBuilder();
        return;
      }

      if (
        e.key === "Backspace" &&
        e.shiftKey &&
        store.step !== "idle" &&
        !(store.step === "field" && !store.inProgress.field)
      ) {
        e.preventDefault();
        store.backspaceBuilderStep();
        requestAnimationFrame(() => {
          getInputRef()?.focus();
        });
        return;
      }

      if (
        e.key === "Backspace" &&
        !store.inputText &&
        store.editingFilterId &&
        store.step === "value"
      ) {
        const editingFieldDef = store.inProgress.field
          ? getFilterDefinitionByKey(store.schema, store.inProgress.field)
          : undefined;
        if (editingFieldDef?.suggestions && store.inProgress.value) {
          e.preventDefault();
          store.setValue("");
          store.setInProgress({ valueDisplay: undefined });
          return;
        }
      }

      if (
        e.key === "Backspace" &&
        !store.inputText &&
        store.step === "value" &&
        store.isMultiSelectMode &&
        store.multiValues.length > 0
      ) {
        e.preventDefault();
        const lastValue = store.multiValues[store.multiValues.length - 1];
        if (lastValue) {
          store.removeMultiValue(lastValue);
        }
        return;
      }

      if (
        e.key === "Backspace" &&
        !store.inputText &&
        store.step !== "idle" &&
        !(store.step === "field" && !store.inProgress.field)
      ) {
        e.preventDefault();
        store.backspaceBuilderStep();
        requestAnimationFrame(() => {
          getInputRef()?.focus();
        });
        return;
      }

      if (e.key === "Backspace" && canBackspaceRemoveChip(store)) {
        e.preventDefault();
        store.backspaceRemoveFilter(store.editingFilterId ?? undefined);
        return;
      }

      if (!store.dropdownOpen) {
        if (e.key === "/" || (e.metaKey && e.key === "k")) {
          e.preventDefault();
          store.openFieldSuggest();
          getInputRef()?.focus();
        }
        return;
      }

      let activeSuggestions = suggestions;
      if (store.step === "key") {
        activeSuggestions = suggestions;
      } else if (store.step === "operator") {
        activeSuggestions = store.operatorSuggestions;
      }
      const maxIndex = Math.max(0, activeSuggestions.length - 1);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          store.setKeyboardIndex(Math.min(store.keyboardIndex + 1, maxIndex));
          break;
        case "ArrowUp":
          e.preventDefault();
          store.setKeyboardIndex(Math.max(store.keyboardIndex - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (store.step === "key") {
            const trimmedKey = store.inputText.trim();
            if (
              trimmedKey &&
              (activeSuggestions.length === 0 || isAwaitingCurrentInputSuggestions)
            ) {
              store.selectMetadataKey(trimmedKey);
              break;
            }
          }
          if (!isAwaitingCurrentInputSuggestions && activeSuggestions.length > 0) {
            const idx = Math.min(store.keyboardIndex, maxIndex);
            const item = activeSuggestions[idx];
            if (item) {
              if (isPlainTextSearchSuggestion(item)) {
                store.setSearch(item.value);
                store.cancelBuilder();
                return;
              }
              selectSuggestion(store, item, {
                nestedKeyMode,
                isPromptField,
                isTraceVersionedEntityField,
                versionStep: promptVersionStep,
              });
              requestAnimationFrame(() => {
                getInputRef()?.focus();
              });
            }
          }
          break;
        case "Escape":
          e.preventDefault();
          if (store.step === "field") {
            getInputRef()?.focus();
            break;
          }
          store.setDropdownOpen(false);
          store.cancelEdit();
          break;
        default:
          break;
      }
    },
    [
      getInputRef,
      isAwaitingCurrentInputSuggestions,
      isPromptField,
      isTraceVersionedEntityField,
      nestedKeyMode,
      promptVersionStep,
      store,
      suggestions,
    ],
  );

  return { handleKeyDown };
};
